import React from "react";
import { fmtMins } from "../../../components/classic/micro";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { inks, paper } from "../viz";

/** Shared look for every drill — numbers, beats, timeline, status. No paragraphs. */

export function splitBeats(text) {
  return String(text || "")
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+(?=[A-Z0-9“"])/)
    .map((s) => s.trim())
    .filter((s) => s.length > 6);
}

export function WaitLine({ t, text }) {
  return <div style={{ fontSize: 13.5, color: t.c.textTertiary, lineHeight: 1.5, padding: "6px 0" }}>{text}</div>;
}

export function StreamWait({ t, lines = 2 }) {
  const ppr = paper(t);
  const vars = { "--tgw-sh0": ppr.well, "--tgw-sh1": t.c.primarySoft || t.c.border };
  return (
    <div style={{ display: "grid", gap: 10, padding: "6px 0" }}>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="tgw-shimmer" style={{ ...vars, height: i % 2 ? 12 : 14, width: i % 2 ? "46%" : "74%" }} />
      ))}
    </div>
  );
}

/**
 * Row-shaped loading placeholders — one shimmer, shaped like the truth.
 * Same geometry as WorkRows rows (rounded, gapped), same .tgw-shimmer sweep
 * as every other wait state, so loading never introduces a second motion
 * system. Theme comes from useDashTheme() so callers pass no props at all.
 */
export function SkeletonRows({ count = 4, height = 42 }) {
  const t = useDashTheme();
  const ppr = paper(t);
  const vars = { "--tgw-sh0": ppr.well, "--tgw-sh1": t.c.primarySoft || t.c.border };
  return (
    <div aria-hidden="true" style={{ display: "flex", flexDirection: "column", gap: 6, padding: "6px 0" }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="tgw-shimmer" style={{ ...vars, height, borderRadius: 10 }} />
      ))}
    </div>
  );
}

export function Chip({ t, children, tone }) {
  const k = inks(t);
  const color = tone === "gold" ? k.gold : tone === "violet" ? k.violet : k.blue;
  return (
    <span style={{
      fontSize: 11.5, padding: "3px 9px", borderRadius: 99, fontWeight: 650,
      background: paper(t).well, color, fontVariantNumeric: "tabular-nums",
    }}>{children}</span>
  );
}

/**
 * The drill's metric tiles. Same card role and same label as the dashboard's
 * hero tile — a metric must not change its clothes just because you clicked
 * into it. Was radius 14, flat, with a grey 10/800/0.08em label while the
 * dashboard ran 24 / raised / blue 11/800/1.4.
 */
export function KpiStrip({ t, items }) {
  const k = inks(t);
  const list = (items || []).filter(Boolean);
  if (!list.length) return null;
  return (
    <div className="tgw-pair" style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(list.length, 4)}, 1fr)`, gap: 10, marginBottom: 12 }}>
      {list.map((it) => (
        <div key={it.label} style={{
          padding: "14px 16px", borderRadius: t.r.card, background: paper(t).card,
          border: `1px solid ${t.c.border}`, boxShadow: t.shadow.raised,
        }}>
          <div style={{ ...t.font.statLabel, color: k.blue }}>{it.label}</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.04em" }}>{it.value}</div>
          {it.sub ? <div style={{ fontSize: 11.5, color: t.c.textSecondary, marginTop: 3 }}>{it.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}

export function BeatList({ t, beats, text, running }) {
  const k = inks(t);
  const rows = (beats && beats.length) ? beats : splitBeats(text);
  if (!rows.length) {
    return running ? (
      <>
        <WaitLine t={t} text="Writing this in plain language…" />
        <StreamWait t={t} lines={3} />
      </>
    ) : null;
  }
  /* Tight hairline beats — the boxed grey rows read as eight identical
     bricks; the number alone carries the sequence, so the boxes go. */
  return (
    <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column" }}>
      {rows.map((raw, i) => {
        const b = typeof raw === "string" ? raw : (raw.text || "");
        return (
          <li key={i} className="tgw-a" style={{
            display: "grid", gridTemplateColumns: "26px 1fr", gap: 10, alignItems: "start",
            padding: "9px 2px", borderTop: i === 0 ? "none" : `1px solid ${t.c.border}`,
          }}>
            <span style={{
              fontSize: 11, fontWeight: 800, fontVariantNumeric: "tabular-nums",
              color: k.violet, paddingTop: 3, letterSpacing: "0.02em",
            }}>{String(i + 1).padStart(2, "0")}</span>
            <span style={{ fontSize: 14, lineHeight: 1.55, maxWidth: "72ch" }}>{b}</span>
          </li>
        );
      })}
      {running ? (
        <li style={{ borderTop: rows.length ? `1px solid ${t.c.border}` : "none", paddingTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: t.c.textTertiary, padding: "4px 4px 8px" }}>
            <i className="tgw-live" /> Still arriving…
          </div>
          <StreamWait t={t} lines={2} />
        </li>
      ) : null}
    </ol>
  );
}

export function WorkRows({ t, rows, onOpen }) {
  const k = inks(t);
  const ppr = paper(t);
  if (!rows?.length) return null;
  return (
    <div>
      <div style={{
        display: "grid", gridTemplateColumns: "28px 1fr auto", gap: 12,
        padding: "0 4px 8px", fontSize: 10, fontWeight: 800, letterSpacing: "0.08em",
        textTransform: "uppercase", color: t.c.textTertiary,
      }}>
        <span>#</span><span>What happened</span><span>Open</span>
      </div>
      {rows.map((row, i) => (
        <button
          key={row.key || `${row.title}-${i}`}
          type="button"
          onClick={() => onOpen?.(row, i)}
          disabled={!onOpen}
          style={{
            display: "grid", gridTemplateColumns: "28px 1fr auto", gap: 12, alignItems: "start",
            width: "100%", textAlign: "left", border: 0, borderTop: `1px solid ${t.c.border}`,
            background: "none", padding: "13px 4px",
            cursor: onOpen ? "pointer" : "default", fontFamily: "inherit", color: "inherit",
          }}
        >
          <span style={{
            width: 28, height: 28, borderRadius: 8, display: "grid", placeItems: "center",
            fontSize: 11, fontWeight: 800, fontVariantNumeric: "tabular-nums",
            background: ppr.well, color: k.blue,
          }}>{String(i + 1).padStart(2, "0")}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 15, fontWeight: 700 }}>{row.title}</span>
            {row.detail ? (
              <span style={{ display: "block", marginTop: 4, fontSize: 13, lineHeight: 1.45, color: t.c.textSecondary }}>{row.detail}</span>
            ) : null}
            {row.who?.length ? (
              <span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                {row.who.map((w) => <Chip key={w} t={t}>{w}</Chip>)}
              </span>
            ) : null}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: k.blue, whiteSpace: "nowrap", paddingTop: 6 }}>
            {row.cue || (onOpen ? "›" : "")}
          </span>
        </button>
      ))}
    </div>
  );
}

export function WhenWhatTable({ t, rows, onOpen }) {
  const k = inks(t);
  if (!rows?.length) return null;
  return (
    <div>
      <div style={{
        display: "grid", gridTemplateColumns: "88px 1fr 56px", gap: 12,
        padding: "0 4px 8px", fontSize: 10, fontWeight: 800, letterSpacing: "0.08em",
        textTransform: "uppercase", color: t.c.textTertiary,
      }}>
        <span>When</span><span>What happened</span><span style={{ textAlign: "right" }}>Time</span>
      </div>
      {rows.map((st, i) => (
        <button
          key={st.key || `${st.userId}-${st.startMs || st.day}-${i}`}
          type="button"
          onClick={() => onOpen?.(st, i)}
          disabled={!onOpen}
          style={{
            display: "grid", gridTemplateColumns: "88px 1fr 56px", gap: 12, alignItems: "center",
            width: "100%", textAlign: "left", border: 0, borderTop: `1px solid ${t.c.border}`,
            background: "none", padding: "11px 4px",
            cursor: onOpen ? "pointer" : "default", fontFamily: "inherit", color: "inherit",
          }}
        >
          <span>
            <b style={{ display: "block", fontVariantNumeric: "tabular-nums", color: k.blue, fontSize: 12.5 }}>
              {st.when || st.day?.slice(5) || st.t0 || "—"}
            </b>
            <span style={{ fontSize: 11, color: t.c.textTertiary }}>{st.who || st.name?.split(" ")[0] || ""}</span>
          </span>
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 14, fontWeight: 650 }}>{st.what || st.title || "A sitting"}</span>
            {st.sub ? <span style={{ fontSize: 12, color: t.c.textTertiary }}>{st.sub}</span> : null}
          </span>
          <b style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: k.blue, fontSize: 13 }}>
            {st.timeLabel || fmtMins(st.minutes)}
          </b>
        </button>
      ))}
    </div>
  );
}

export function Timeline({ t, phases }) {
  const k = inks(t);
  if (!phases?.length) return null;
  return (
    <div style={{ paddingLeft: 2 }}>
      {phases.map((p, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "22px 72px 1fr", gap: 10, alignItems: "start", padding: "11px 0" }}>
          <span style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <i style={{
              width: 10, height: 10, borderRadius: 99, background: k.blue, marginTop: 4,
              boxShadow: `0 0 0 3px ${k.blue}22`,
            }} />
            {i < phases.length - 1 ? <i style={{ width: 1, flex: 1, minHeight: 22, background: t.c.border, marginTop: 4 }} /> : null}
          </span>
          <span>
            <b style={{ display: "block", fontVariantNumeric: "tabular-nums", color: k.blue, fontSize: 13 }}>{p.time}</b>
            {p.who ? <span style={{ fontSize: 11.5, color: t.c.textTertiary }}>{p.who}</span> : null}
          </span>
          <span>
            <span style={{ display: "block", fontSize: 14.5, fontWeight: 650 }}>{p.what}</span>
            {p.evidence ? <span style={{ display: "block", fontSize: 12.5, color: t.c.textSecondary, marginTop: 3 }}>{p.evidence}</span> : null}
            {p.items?.length ? (
              <span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                {p.items.map((it) => <Chip key={it} t={t}>{it}</Chip>)}
              </span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export function OutcomeCards({ t, outcomes }) {
  const k = inks(t);
  const ppr = paper(t);
  if (!outcomes?.length) return null;
  const label = { done: "Done", progress: "In progress", open: "Still open" };
  const color = { done: k.blue, progress: k.gold, open: k.violet };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 8 }}>
      {outcomes.map((o, i) => (
        <div key={i} style={{ padding: "12px 14px", borderRadius: t.r.well, background: ppr.well }}>
          <span style={{
            ...t.font.microLabel,
            color: color[o.state] || k.blue,
          }}>{label[o.state] || o.state}</span>
          <div style={{ fontSize: 14, fontWeight: 650, marginTop: 6, lineHeight: 1.4 }}>{o.text}</div>
          {o.evidence ? <div style={{ fontSize: 12.5, color: t.c.textSecondary, marginTop: 5 }}>{o.evidence}</div> : null}
        </div>
      ))}
    </div>
  );
}

export function HeadlineBlock({ t, text }) {
  if (!text) return null;
  return (
    <div style={{
      fontSize: 20, fontWeight: 650, lineHeight: 1.4, letterSpacing: "-0.03em",
      marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${t.c.border}`,
    }}>{text}</div>
  );
}
